import { useEffect, useRef, useState } from "hono/jsx";
import { Variant } from "../server/types.ts";

interface ChessBoardProps {
  fen: string;
  variant: Variant;
  _turn: string;
  onMove?: (move: { from: string; to: string }) => void;
  orientation?: "white" | "black" | "red" | "blue";
}

export const ChessBoard = ({ fen, variant, _turn, onMove, orientation = "white" }: ChessBoardProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedSquare, setSelectedSquare] = useState<{r: number, c: number} | null>(null);

  const gridSize = variant === "FOUR_PLAYER" ? 14 : 8;
  const isFourPlayer = variant === "FOUR_PLAYER";

  const getSquareColor = (r: number, c: number) => {
    return (r + c) % 2 === 0 ? "#eeeed2" : "#769656";
  };

  const isSquareValid = (r: number, c: number) => {
    if (!isFourPlayer) return true;
    // Corners are invalid in 4-player
    const inTopLeft = r < 3 && c < 3;
    const inTopRight = r < 3 && c > 10;
    const inBottomLeft = r > 10 && c < 3;
    const inBottomRight = r > 10 && c > 10;
    return !inTopLeft && !inTopRight && !inBottomLeft && !inBottomRight;
  };

  const parseFen = (fenStr: string) => {
    // Simple FEN parser
    // Returns a 2D array of pieces
    const board = Array.from({ length: gridSize }, () => Array(gridSize).fill(null));
    if (isFourPlayer) {
        // 4-player FEN logic would go here
        // For now just empty or placeholder
        return board;
    }
    
    const [position] = fenStr.split(" ");
    const rows = position.split("/");
    
    rows.forEach((row, r) => {
      let c = 0;
      for (const char of row) {
        if (isNaN(parseInt(char))) {
          board[r][c] = char;
          c++;
        } else {
          c += parseInt(char);
        }
      }
    });
    return board;
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const cellSize = (width - 40) / gridSize; // 20px padding for labels
    const padding = 20;

    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = "#2a2a2a";
    ctx.fillRect(0, 0, width, height);

    const board = parseFen(fen);

    // Apply rotation
    let rot = 0;
    if (orientation === "black") rot = Math.PI;
    if (orientation === "red") rot = -Math.PI / 2;
    if (orientation === "blue") rot = Math.PI / 2;

    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate(rot);
    ctx.translate(-width / 2, -height / 2);

    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!isSquareValid(r, c)) continue;

        const x = padding + c * cellSize;
        const y = padding + r * cellSize;

        // Draw square
        ctx.fillStyle = getSquareColor(r, c);
        ctx.fillRect(x, y, cellSize, cellSize);

        // Selection highlight
        if (selectedSquare && selectedSquare.r === r && selectedSquare.c === c) {
            ctx.fillStyle = "rgba(255, 255, 0, 0.5)";
            ctx.fillRect(x, y, cellSize, cellSize);
        }

        // Draw piece (Unicode for now)
        const piece = board[r][c];
        if (piece) {
            ctx.save();
            const centerX = x + cellSize / 2;
            const centerY = y + cellSize / 2;
            ctx.translate(centerX, centerY);
            ctx.rotate(-rot); // Counter-rotate piece
            
            ctx.fillStyle = piece === piece.toUpperCase() ? "#fff" : "#000";
            ctx.font = `${cellSize * 0.8}px serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            
            const unicodeMap: Record<string, string> = {
                'p': '♟', 'r': '♜', 'n': '♞', 'b': '♝', 'q': '♛', 'k': '♚',
                'P': '♟', 'R': '♜', 'N': '♞', 'B': '♝', 'Q': '♛', 'K': '♚'
            };
            
            const glyph = unicodeMap[piece] || piece;
            ctx.fillText(glyph, 0, 0);
            ctx.restore();
        }
      }
    }

    // Draw Labels (Inside rotated context for correct positioning, but counter-rotated for readability)
    ctx.fillStyle = "#aaa";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let i = 0; i < gridSize; i++) {
        // Files (a, b, c...)
        ctx.save();
        const fileX = padding + i * cellSize + cellSize / 2;
        const fileY = height - 10;
        ctx.translate(fileX, fileY);
        ctx.rotate(-rot);
        ctx.fillText(String.fromCharCode(97 + i), 0, 0);
        ctx.restore();

        // Ranks (1, 2, 3...)
        ctx.save();
        const rankX = 10;
        const rankY = padding + i * cellSize + cellSize / 2;
        ctx.translate(rankX, rankY);
        ctx.rotate(-rot);
        ctx.fillText((gridSize - i).toString(), 0, 0);
        ctx.restore();
    }

    ctx.restore();
  };

  useEffect(() => {
    draw();
  }, [fen, variant, selectedSquare, orientation]);

  const handleClick = (e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const width = canvas.width;
    const height = canvas.height;
    const padding = 20;
    const cellSize = (width - 40) / gridSize;

    // Transform mouse coords to grid coords accounting for rotation
    // Translate to center
    const tx = mouseX - width / 2;
    const ty = mouseY - height / 2;

    // Inverse rotation
    let rot = 0;
    if (orientation === "black") rot = Math.PI;
    if (orientation === "red") rot = -Math.PI / 2;
    if (orientation === "blue") rot = Math.PI / 2;

    const cos = Math.cos(-rot);
    const sin = Math.sin(-rot);
    const x = tx * cos - ty * sin + width / 2;
    const y = tx * sin + ty * cos + height / 2;

    const c = Math.floor((x - padding) / cellSize);
    const r = Math.floor((y - padding) / cellSize);

    if (r >= 0 && r < gridSize && c >= 0 && c < gridSize && isSquareValid(r, c)) {
        if (selectedSquare) {
            // Move Attempt
            if (onMove) {
                const fromFile = String.fromCharCode(97 + selectedSquare.c);
                const fromRank = gridSize - selectedSquare.r;
                const toFile = String.fromCharCode(97 + c);
                const toRank = gridSize - r;
                onMove({ from: `${fromFile}${fromRank}`, to: `${toFile}${toRank}` });
            }
            setSelectedSquare(null);
        } else {
            setSelectedSquare({ r, c });
        }
    }
  };

  return (
    <div class="flex flex-col items-center gap-4">
        <canvas 
            ref={canvasRef} 
            width={600} 
            height={600} 
            class="rounded-lg shadow-xl cursor-pointer bg-base-300"
            onClick={handleClick}
        />
        <div class="flex gap-2">
            <span class="text-xs opacity-50 uppercase font-black">Click piece then destination</span>
        </div>
    </div>
  );
};
